/**
 * Central configuration for all datasources in the application.
 * This is the single source of truth for datasource names, aliases, and metadata.
 */

// ============================================================================
// DATASOURCE ENUM
// ============================================================================

/**
 * All available datasources in the system.
 * Use this enum instead of magic strings throughout the codebase.
 */
export enum DataSource {
  // Legislation sources (the four LODA supports, LAR-384)
  Retsinformation = "Retsinformation",
  Domsdatabasen = "Domsdatabasen",
  Arvid = "Arvid",
  Eurlex = "Eurlex",

  // Non-datasource types (for SearchOptionType compatibility)
  Document = "Document",
  Workspace = "Workspace",
  ChatGPT = "ChatGPT",
}

/**
 * The key a tenant's filter configuration uses for a source, in the exact
 * shape the API's `Sources` query parameter accepts.
 *
 * These are *not* `DataSource` members: the filter config uses its own keys
 * (`"retsinformation"`, `"eurlex"`, `"domsdatabasen"`, `"arvid"`), while `DataSource` enumerates
 * display names (`DataSource.Retsinformation === "Retsinformation"`). The API
 * matches the key itself, so the value reaches the `Sources` parameter as-is;
 * whether a caller normalizes first (e.g. via `getDbValue`) is its own choice.
 *
 * A `DataSource` is still assignable here, so callers that genuinely hold one
 * need no conversion.
 */
export type LegislationSourceKey = string;

// ============================================================================
// DATASOURCE CONFIGURATION
// ============================================================================

export interface DataSourceConfig {
  /** Display name for UI */
  displayName: string;
  /** Value stored in database (defaults to displayName if not specified) */
  dbValue?: string;
  /** Origin URL for the source */
  originUrl: string | null;
  /** Lowercase aliases for normalization (including the canonical lowercase form) */
  aliases: string[];
  /** Feature flag name for enabling/disabling this source */
  featureFlag: string | null;
  /** Whether this source supports HTML view */
  supportsHtmlView: boolean;
  /** Whether this source supports paragraph navigation (§ dropdown) */
  supportsParagraphNavigation: boolean;
  /** Label for invalid/historic documents */
  invalidLabel: string | null;
  /** Whether this source is supported in chat (chat-api / retrieval) */
  supportsChat: boolean;
  /** Exclude from legislation browse and global search source pickers (v1 consolidation). */
  hiddenFromBrowse?: boolean;
}

/**
 * Configuration for each datasource.
 * Contains all metadata needed for display, normalization, and feature management.
 */
export const DATASOURCE_CONFIG: Record<DataSource, DataSourceConfig> = {
  // Legislation sources
  [DataSource.Retsinformation]: {
    displayName: "Retsinformation",
    originUrl: "https://www.retsinformation.dk",
    aliases: ["retsinformation"],
    featureFlag: "Kilde-Retsinformation",
    supportsHtmlView: true,
    supportsParagraphNavigation: true,
    invalidLabel: "Historisk",
    supportsChat: true,
  },
  [DataSource.Domsdatabasen]: {
    displayName: "Domsdatabasen",
    originUrl: "https://domsdatabasen.dk/",
    aliases: ["domsdatabasen"],
    featureFlag: "Kilde-Domsdatabasen",
    supportsHtmlView: true,
    supportsParagraphNavigation: false,
    invalidLabel: "Ugyldig",
    supportsChat: true,
  },
  [DataSource.Arvid]: {
    displayName: "ARVID",
    dbValue: "Arvid",
    originUrl: "https://arvid.star.dk/",
    aliases: ["arvid"],
    featureFlag: "Kilde-Arvid",
    supportsHtmlView: true,
    supportsParagraphNavigation: false,
    invalidLabel: "Ophævet",
    supportsChat: true,
  },
  [DataSource.Eurlex]: {
    displayName: "EUR-Lex",
    dbValue: "EURLex",
    originUrl: "https://eur-lex.europa.eu",
    aliases: ["eurlex", "eur-lex", "eurlx"],
    featureFlag: "Kilde-Eurlex",
    supportsHtmlView: true,
    supportsParagraphNavigation: false,
    invalidLabel: null,
    supportsChat: true,
  },

  // Non-datasource types (for SearchOptionType compatibility)
  [DataSource.Document]: {
    displayName: "Document",
    originUrl: null,
    aliases: ["document"],
    featureFlag: null,
    supportsHtmlView: false,
    supportsParagraphNavigation: false,
    invalidLabel: null,
    supportsChat: true,
  },
  [DataSource.Workspace]: {
    displayName: "Workspace",
    originUrl: null,
    aliases: ["workspace"],
    featureFlag: null,
    supportsHtmlView: false,
    supportsParagraphNavigation: false,
    invalidLabel: null,
    supportsChat: true,
  },
  [DataSource.ChatGPT]: {
    displayName: "ChatGPT",
    originUrl: null,
    aliases: ["chatgpt"],
    featureFlag: null,
    supportsHtmlView: false,
    supportsParagraphNavigation: false,
    invalidLabel: null,
    supportsChat: false,
  },
};

// ============================================================================
// DERIVED CONSTANTS
// ============================================================================

/**
 * Sources that support paragraph navigation (§ dropdown)
 */
export const PARAGRAPH_NAVIGATION_SUPPORTED_SOURCES = Object.entries(
  DATASOURCE_CONFIG
)
  .filter(([, config]) => config.supportsParagraphNavigation)
  .map(([key]) => DATASOURCE_CONFIG[key as DataSource].displayName);

/**
 * Origin URLs by display name (for backward compatibility)
 */
export const ORIGIN_BY_SOURCE: Record<string, string> = Object.fromEntries(
  Object.entries(DATASOURCE_CONFIG)
    .filter(([, config]) => config.originUrl !== null)
    .map(([, config]) => [config.displayName, config.originUrl as string])
);

// ============================================================================
// NORMALIZATION
// ============================================================================

/**
 * Normalize Danish characters for comparison
 */
const normalizeDanishChars = (str: string): string => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove diacritics
    .replace(/- /g, " ")
    .replace(/Æ|Ä|AE/gi, "æ")
    .replace(/Ø|Ö|OE/gi, "ø")
    .replace(/Å|AA/gi, "å")
    .toLowerCase();
};

// Build alias lookup map at module load time
const aliasToDataSource = new Map<string, DataSource>();
Object.entries(DATASOURCE_CONFIG).forEach(([key, config]) => {
  const dataSource = key as DataSource;
  config.aliases.forEach((alias) => {
    aliasToDataSource.set(alias.toLowerCase(), dataSource);
    aliasToDataSource.set(normalizeDanishChars(alias), dataSource);
  });
  // Also add the display name as an alias
  aliasToDataSource.set(config.displayName.toLowerCase(), dataSource);
  aliasToDataSource.set(normalizeDanishChars(config.displayName), dataSource);
});

/**
 * Normalize any string input to a DataSource enum value.
 * Handles case-insensitivity, Danish character variants, and aliases.
 *
 * @param input - The string to normalize (e.g., "eur-lex", "ARVID", "Retsinformation")
 * @returns The matching DataSource enum value, or null if not found
 */
export const normalizeDataSource = (
  input: string | null | undefined
): DataSource | null => {
  if (!input) return null;

  const trimmed = input.trim();
  const lower = trimmed.toLowerCase();
  const normalized = normalizeDanishChars(trimmed);

  // Try direct lookup
  if (aliasToDataSource.has(lower)) {
    return aliasToDataSource.get(lower)!;
  }

  // Try normalized lookup
  if (aliasToDataSource.has(normalized)) {
    return aliasToDataSource.get(normalized)!;
  }

  return null;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the origin URL for a datasource
 */
export const getOriginUrl = (
  source: DataSource | string | null | undefined
): string | null => {
  if (!source) return null;

  // If it's a display name string, try to find it in ORIGIN_BY_SOURCE first
  if (typeof source === "string" && source in ORIGIN_BY_SOURCE) {
    return ORIGIN_BY_SOURCE[source];
  }

  const normalized =
    typeof source === "string" && source in DataSource
      ? (source as DataSource)
      : normalizeDataSource(source as string);

  if (!normalized) return null;
  return DATASOURCE_CONFIG[normalized]?.originUrl ?? null;
};

/**
 * Whether a datasource should be hidden from legislation browse and search pickers.
 */
export const isHiddenFromBrowse = (
  source: DataSource | string | null | undefined
): boolean => {
  if (!source) return false;

  const normalized =
    typeof source === "string" && source in DataSource
      ? (source as DataSource)
      : normalizeDataSource(source as string);

  if (!normalized) return false;
  return DATASOURCE_CONFIG[normalized]?.hiddenFromBrowse === true;
};

/**
 * Get the feature flag for a datasource
 */
export const getFeatureFlag = (
  source: DataSource | string | null | undefined
): string | null => {
  if (!source) return null;

  const normalized =
    typeof source === "string" && source in DataSource
      ? (source as DataSource)
      : normalizeDataSource(source as string);

  if (!normalized) return null;
  return DATASOURCE_CONFIG[normalized]?.featureFlag ?? null;
};

/**
 * Get the invalid/historic label for a datasource
 */
export const getInvalidLabel = (
  source: DataSource | string | null | undefined
): string => {
  if (!source) return "Ikke-gældende";

  const normalized =
    typeof source === "string" && source in DataSource
      ? (source as DataSource)
      : normalizeDataSource(source as string);

  if (!normalized) return "Ikke-gældende";
  return DATASOURCE_CONFIG[normalized]?.invalidLabel ?? "Ikke-gældende";
};

/**
 * Get the display name for a datasource
 */
export const getDisplayName = (
  source: DataSource | string | null | undefined
): string | null => {
  if (!source) return null;

  const normalized =
    typeof source === "string" && source in DataSource
      ? (source as DataSource)
      : normalizeDataSource(source as string);

  if (!normalized) return null;
  return DATASOURCE_CONFIG[normalized]?.displayName ?? null;
};

/**
 * Check if a datasource supports paragraph navigation
 */
export const supportsParagraphNavigation = (
  source: DataSource | string | null | undefined
): boolean => {
  if (!source) return false;

  const normalized =
    typeof source === "string" && source in DataSource
      ? (source as DataSource)
      : normalizeDataSource(source as string);

  if (!normalized) return false;
  return DATASOURCE_CONFIG[normalized]?.supportsParagraphNavigation ?? false;
};

/**
 * Check if a datasource is supported in chat (chat-api / retrieval)
 */
export const supportsChat = (
  source: DataSource | string | null | undefined
): boolean => {
  if (!source) return false;

  const normalized =
    typeof source === "string" && source in DataSource
      ? (source as DataSource)
      : normalizeDataSource(source as string);

  if (!normalized) return false;
  return DATASOURCE_CONFIG[normalized]?.supportsChat ?? false;
};

/**
 * Get the database value for a datasource.
 * Some sources have different names in the DB than their display names.
 * Defaults to displayName if dbValue is not explicitly set.
 */
export const getDbValue = (
  source: DataSource | string | null | undefined
): string | null => {
  if (!source) return null;

  const normalized =
    typeof source === "string" && source in DataSource
      ? (source as DataSource)
      : normalizeDataSource(source as string);

  if (!normalized) return null;
  const config = DATASOURCE_CONFIG[normalized];
  return config?.dbValue ?? config?.displayName ?? null;
};
